import React, { useState } from 'react';
import { 
  Smartphone, 
  Phone, 
  KeyRound, 
  ShieldCheck, 
  Lock, 
  Laptop, 
  HelpCircle,
  ChevronDown,
  CheckCircle,
  Shield
} from 'lucide-react';

export default function MethodsList({ methods = [], translations: t }) {
  const [isExpanded, setIsExpanded] = useState(true);

  const getMethodIcon = (type) => {
    switch (type) {
      case 'authenticator':
        return <Smartphone className="w-5 h-5 text-[#008446]" />;
      case 'phone':
        return <Phone className="w-5 h-5 text-amber-600" />;
      case 'fido':
        return <KeyRound className="w-5 h-5 text-[#008446]" />;
      case 'softwareOath':
        return <Shield className="w-5 h-5 text-purple-600" />;
      case 'windowsHello':
        return <Laptop className="w-5 h-5 text-teal-600" />;
      case 'password':
        return <Lock className="w-5 h-5 text-slate-400" />;
      default:
        return <HelpCircle className="w-5 h-5 text-slate-400" />;
    }
  };

  const getMethodBadge = (method) => {
    if (method.isPreferred) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#dbf1e4] text-[#005930] border border-[#8cceaa]">
          <CheckCircle className="w-3 h-3 text-[#008446]" />
          {t.badgeRecommended}
        </span>
      );
    }
    if (method.isLegacy) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
          {t.badgeLegacy}
        </span>
      );
    }
    if (method.isMfa) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
          {t.badgeActive}
        </span>
      );
    }
    if (method.type === 'password') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
          {t.badgePrimary}
        </span>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
      
      {/* Header with expand toggle */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors border-b border-slate-100"
      >
        <div className="flex items-center gap-2.5">
          <ShieldCheck className="w-5 h-5 text-slate-500" />
          <h3 className="text-base font-semibold text-slate-800">
            {t.methodsHeader}
          </h3>
          <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-slate-100 text-slate-600">
            {methods.length}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
      </button>

      {isExpanded && (
        <div className="p-6">
          <p className="text-xs text-slate-500 mb-4">
            {t.methodsDescription}
          </p>

          {methods.length === 0 ? (
            <p className="text-sm text-slate-500 italic py-3">
              {t.noMethodsFound}
            </p>
          ) : (
            <div className="divide-y divide-slate-100">
              {methods.map((method, index) => {
                const label = t[method.nameKey] || method.type;
                return (
                  <div key={method.id || index} className="py-3.5 flex items-center justify-between gap-4 first:pt-0 last:pb-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
                        {getMethodIcon(method.type)}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-800 truncate">
                            {label}
                          </p>
                        </div>
                        {method.detail && (
                          <p className="text-xs text-slate-500 truncate">
                            {method.detail}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="shrink-0">
                      {getMethodBadge(method)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
